<template>
  <div>
    <h1 class="main-header">
      <translate translate-context="email_schedules">Email Schedules</translate>
    </h1>

    <CCard class="no-borders">
      <CCardHeader>
        <div class="card-header-actions">
          <CButton
            v-if="canCreate"
            color="primary"
            :to="{ name: createRouteName }">
            <CIcon name="cil-plus" class="mr-1"/>
            <translate translate-context="email_schedules">Create Schedule</translate>
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody>
        <CDataTable
          :items="schedules"
          :fields="fields"
          :loading="loading"
          :hover="false"
          :striped="true"
          :bordered="true"
          :small="false"
          :fixed="false"
          class="custom-table">

          <template #schedule_type="{item}">
            <td>
              <strong>{{ getJobTypeLabel(item.schedule_type) }}</strong>
              <br>
              <small class="text-muted">{{ item.schedule_type }}</small>
            </td>
          </template>

          <template #schedule_period="{item}">
            <td>
              <div v-if="item.schedule_from">
                <strong><translate translate-context="email_schedules">From:</translate></strong> {{ formatDate(item.schedule_from) }}
              </div>
              <div v-if="item.schedule_to">
                <strong><translate translate-context="email_schedules">To:</translate></strong> {{ formatDate(item.schedule_to) }}
              </div>
              <div v-if="item.send_time">
                <strong><translate translate-context="email_schedules">Send at:</translate></strong> {{ item.send_time }}
              </div>
              <div v-if="item.send_every">
                <strong><translate translate-context="email_schedules">Every:</translate></strong> {{ item.send_every }}h
              </div>
            </td>
          </template>

          <template #enabled="{item}">
            <td class="text-center">
              <CBadge :color="item.enabled ? 'success' : 'danger'">
                {{ item.enabled ? $pgettext('email_schedules.status', 'Enabled') : $pgettext('email_schedules.status', 'Disabled') }}
              </CBadge>
            </td>
          </template>

          <template #email_sent_count="{item}">
            <td class="text-center">
              {{ item.email_sent_count || 0 }}
            </td>
          </template>

          <template #actions="{item}">
            <td class="text-right">
              <CButton
                v-if="canEdit"
                variant="outline"
                color="primary"
                :to="{ name: editRouteName, params: { scheduleId: item.id } }"
                v-translate translate-context="list.actions">
                Edit
              </CButton>

              <CButton
                v-if="canDelete"
                variant="outline"
                color="danger"
                class="ml-2"
                @click="onDelete(item)"
                v-translate translate-context="list.actions">
                Delete
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import {getAllJobTypes, getJobTypeLabel} from '@/domain/core/constant/emailJobTypes'
import moment from 'moment'

export default {
  name: 'ListEmailSchedules',
  data() {
    return {
      loading: true,
      fields: [
        {
          key: 'schedule_type',
          label: this.$pgettext('email_schedules', 'Schedule Type'),
          _style: 'width: 25%'
        },
        {
          key: 'schedule_period',
          label: this.$pgettext('email_schedules', 'Schedule Period'),
          _style: 'width: 30%'
        },
        {
          key: 'enabled',
          label: this.$pgettext('email_schedules', 'Status'),
          _style: 'width: 10%'
        },
        {
          key: 'email_sent_count',
          label: this.$pgettext('email_schedules', 'Emails Sent'),
          _style: 'width: 10%'
        },
        {
          key: 'actions',
          label: '',
          _style: 'width: 150px'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('emailSchedules', ['schedules']),
    ...mapGetters('login', ['hasPerm']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      }
      if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      if (this.scopeType === 'event-owner') {
        return this.$route.params.eventOwnerId
      }
      return null
    },

    createRouteName() {
      const names = {
        'global': 'admin.emailSchedules.new',
        'contract-manager': 'contractManager.emailSchedules.new',
        'event-owner': 'eventOwner.emailSchedules.new',
        'pica-service': 'event.emailSchedules.new'
      }
      return names[this.scopeType]
    },

    editRouteName() {
      const names = {
        'global': 'admin.emailSchedules.edit',
        'contract-manager': 'contractManager.emailSchedules.edit',
        'event-owner': 'eventOwner.emailSchedules.edit',
        'pica-service': 'event.emailSchedules.edit'
      }
      return names[this.scopeType]
    },

    canCreate() {
      const perms = {
        'global': 'admin.email_scheduler.create',
        'contract-manager': 'contract_manager.email_scheduler.create',
        'event-owner': 'event_owner.email_scheduler.create',
        'pica-service': 'email_scheduler.create'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    canEdit() {
      const perms = {
        'global': 'admin.email_scheduler.edit',
        'contract-manager': 'contract_manager.email_scheduler.edit',
        'event-owner': 'event_owner.email_scheduler.edit',
        'pica-service': 'email_scheduler.edit'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    canDelete() {
      const perms = {
        'global': 'admin.email_scheduler.delete',
        'contract-manager': 'contract_manager.email_scheduler.delete',
        'event-owner': 'event_owner.email_scheduler.delete',
        'pica-service': 'email_scheduler.delete'
      }
      return this.hasPerm(perms[this.scopeType])
    }
  },
  async mounted() {
    try {
      await this.setScope({type: this.scopeType, id: this.scopeId})

      // Fetch config based on scope
      const configTypes = {
        'global': 'email_scheduler_global',
        'contract-manager': 'email_scheduler_contract_manager',
        'event-owner': 'email_scheduler_event_owner',
        'pica-service': 'email_scheduler_pica_service'
      }

      await this.fetchConfig({
        type: configTypes[this.scopeType],
        id: this.scopeId
      })
    } catch (error) {
      this.notifyError(this.$pgettext('email_schedules', 'Failed to load email schedules'))
    }
    this.loading = false
  },
  methods: {
    ...mapActions('emailSchedules', ['setScope', 'deleteEmailSchedule']),
    ...mapActions('login', ['fetchConfig']),

    getJobTypeLabel(jobType) {
      return getJobTypeLabel(jobType)
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    getStatusColor(status) {
      const colors = {
        'scheduled': 'info',
        'active': 'success',
        'pending': 'warning',
        'completed': 'secondary',
        'paused': 'dark'
      }
      return colors[status] || 'secondary'
    },

    getStatusLabel(status) {
      const labels = {
        'scheduled': this.$pgettext('email_schedules.status', 'Scheduled'),
        'active': this.$pgettext('email_schedules.status', 'Active'),
        'pending': this.$pgettext('email_schedules.status', 'Pending'),
        'completed': this.$pgettext('email_schedules.status', 'Completed'),
        'paused': this.$pgettext('email_schedules.status', 'Paused')
      }
      return labels[status] || status
    },

    onDelete(item) {
      this.confirm({
        title: this.$pgettext('email_schedules', 'Delete Schedule'),
        message: this.$pgettext('email_schedules', 'Are you sure you want to delete this schedule?'),
        cb_confirm: async () => {
          try {
            await this.deleteEmailSchedule(item.id)
            this.notifySuccess(this.$pgettext('email_schedules', 'Schedule deleted successfully'))
          } catch (error) {
            this.notifyError(this.$pgettext('email_schedules', 'Failed to delete schedule'))
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.main-header {
  margin-bottom: 1.5rem;
}
</style>
